/** Геометрия пончика: где стоят дуги, значки и подписи.
 *
 * Вынесено из Donut.tsx отдельным модулем, чтобы раскладку можно было проверять
 * без браузера. Наложение подписи на значок глазом ловится только на тех данных,
 * которые случайно открыл; тут те же наложения ищутся на сотнях распределений за
 * миллисекунды. Модуль не знает ни про React, ни про DOM — на входе доли в
 * процентах, на выходе числа.
 *
 * Порядок долек = порядок массива, доли считаем идущими подряд от 12 часов по
 * часовой стрелке — как их и рисует Donut.
 */

const BOX = 264;          // место под подписи снаружи кольца
const MID = BOX / 2;
const R = 74;
const STROKE = 26;
const RING_EDGE = R + STROKE / 2;   // внешний край кольца
const RING_INNER = R - STROKE / 2;
const GAP = 3;            // px по окружности, чтобы дольки не сливались
//: Просвет между кольцом и БЛИЖНИМ краем подписи. Радиус тут задаёт точку
//: привязки, а не центр текста: центрированная на луче подпись заходит на
//: кольцо углом — «25%» шириной 27px залезала внутрь на 1.5px, и это было видно.
const LABEL_PAD = 6;
const LABEL_R = RING_EDGE + LABEL_PAD;
const MIN_LABEL = 3;      // ниже трёх процентов подпись врёт о размере дольки
//: Значку нужна дуга, в которую он поместится. Порог в ПИКСЕЛЯХ, а не в
//: процентах: процент ничего не говорит о том, влезет ли рисунок, — при семи
//: дольках «11%» это 51px, а при трёх те же 11% дают ту же дугу другой длины
//: только в другом кольце. Шестнадцатипиксельному глифу нужно ~30px дуги.
const MIN_ICON_ARC = 30;
//: Сколько дольки можно отдать под зазор. Без потолка зазор в 3px съедает у
//: однопроцентной дольки треть её длины, и пирог начинает врать о мелочи в свою
//: пользу — ровно там, где и так плохо видно.
const MAX_GAP_SHARE = 0.35;
//: Совсем схлопнуть дугу нельзя: нулевой dash браузер не рисует вовсе, и долька
//: пропадает из пирога целиком вместо того, чтобы остаться волоском.
const MIN_VISIBLE = 0.5;

const ICON_VIEW = 24;     // контур значка нарисован в квадрате 24×24
const ICON_SCALE = 0.667;
const ICON_SIZE = ICON_VIEW * ICON_SCALE;
//: Половина стороны глифа, на которую сдвигаем его от середины дуги. Округлено
//: до целого пикселя, а не до ICON_SIZE/2 — так значок садится на пиксельную
//: сетку, а несимметричность в 4 тысячных пикселя не видна ничем.
const ICON_SHIFT = 8;

const LABEL_FONT = 12;
//: Высота цифры, а не кегля: в «42%» нет ни выносных элементов вниз, ни
//: диакритики вверх, и коробка в 12px завысила бы наложения по вертикали.
//: Замер actualBoundingBox для «42%» дал 8.79px — оценка сходится.
const LABEL_CAP = LABEL_FONT * 0.72;
//: ОЦЕНКА, НЕ ИЗМЕРЕНИЕ. Ширину глифа знает только шрифт, а DOM тут недоступен;
//: берём ~7px на символ при fontSize=12/fontWeight=600. Цифры не моноширинные,
//: так что число заведомо не точное. Оценка ЗАНИЖЕННАЯ — см. measuredWidth.
const LABEL_CHAR = 7;

/** Ширина подписи по оценке в LABEL_CHAR пикселей на символ. */
export function estimateWidth(text: string): number {
  return text.length * LABEL_CHAR;
}

//: А это уже ЗАМЕР: canvas measureText на «600 12px -apple-system, …» — том же
//: стеке, что стоит в tokens.css. Держим рядом с оценкой, потому что разница
//: принципиальная: «%» занимает 11.99px, а не 7px, и подпись «3%» на деле шире
//: оценки в полтора раза. Числа привязаны к SF Pro; на Roboto и Segoe UI они
//: другие, поэтому в раскладку по умолчанию не идут.
const ADVANCE: Record<string, number> = {
  "0": 7.998, "1": 5.947, "2": 7.559, "3": 7.857, "4": 8.068,
  "5": 7.781, "6": 8.016, "7": 7.113, "8": 8.104, "9": 8.016, "%": 11.988,
};

/** Ширина подписи по замеренным метрикам системного шрифта macOS/iOS.
 *
 *  Это модель ПО УМОЛЧАНИЮ, а грубая оценка осталась только для сравнения.
 *  Оценка в 7px на символ занижала «%» почти вдвое и потому пропускала реальные
 *  наложения: «3%» на деле 19.85px, а не 14.
 *
 *  Числа сняты с SF Pro. На Android шрифт Roboto — там и цифры, и «%» УЖЕ, так
 *  что раскладка, разведённая по этим числам, на Android разойдётся с запасом.
 *  Ошибаться в сторону широкого шрифта безопасно, в сторону узкого — нет.
 */
export function measuredWidth(text: string): number {
  return [...text].reduce((sum, ch) => sum + (ADVANCE[ch] ?? LABEL_CHAR), 0);
}

export type TextAnchor = "start" | "middle" | "end";
export type TextBaseline = "hanging" | "alphabetic" | "central";

/** Габарит в координатах viewBox: левый-верхний и правый-нижний углы. */
export type Rect = { x0: number; y0: number; x1: number; y1: number };

export type ArcPlacement = {
  radius: number;
  /** Угол середины СЕКТОРА (не видимой дуги), радианы от 12 часов по часовой. */
  mid: number;
  /** Углы начала и конца ВИДИМОЙ дуги — того, что реально закрашено. */
  start: number;
  end: number;
  /** Длина видимой дуги и съеденный зазор, px по окружности. */
  visible: number;
  gap: number;
  dash: string;
  dashOffset: number;
};

export type IconPlacement = {
  /** Точка translate: левый-верхний угол квадрата глифа. */
  x: number;
  y: number;
  scale: number;
  size: number;
  cx: number;
  cy: number;
  box: Rect;
};

export type LabelPlacement = {
  text: string;
  /** Точка привязки текста; куда от неё растёт текст, говорят anchor/baseline. */
  x: number;
  y: number;
  anchor: TextAnchor;
  baseline: TextBaseline;
  /** Оценка габаритов, см. LABEL_CHAR и LABEL_CAP. */
  width: number;
  height: number;
  box: Rect;
};

export type SlicePlacement = {
  share: number;
  arc: ArcPlacement;
  /** null — дуга короче MIN_ICON_ARC, значок не рисуется. */
  icon: IconPlacement | null;
  /** null — доля ниже MIN_LABEL, подпись не рисуется. */
  label: LabelPlacement | null;
};

export type DonutLayout = {
  box: number;
  center: number;
  radius: number;
  stroke: number;
  ringInner: number;
  ringOuter: number;
  circumference: number;
  slices: SlicePlacement[];
};

/** Длина по окружности → угол в радианах. */
function turn(length: number, circumference: number): number {
  return (length / circumference) * 2 * Math.PI;
}

/** Раскладка по долям в процентах. Второй аргумент — модель ширины текста:
 *  по умолчанию грубая оценка, для проверок можно подставить замер. На отрисовку
 *  он не влияет — SVG сам меряет текст, ширина нужна только для поиска наложений. */
export function donutLayout(
  shares: number[],
  width: (text: string) => number = measuredWidth,
): DonutLayout {
  const circumference = 2 * Math.PI * R;
  let offset = 0;
  const slices = shares.map((share) => {
    const length = (circumference * share) / 100;
    // Зазор делим ПОПОЛАМ между соседями: раньше он весь уходил в конец дольки,
    // и видимая дуга оказывалась сдвинута назад на пол-зазора относительно
    // своего сектора — при семи дольках это заметный перекос.
    const gap = Math.min(GAP, length * MAX_GAP_SHARE);
    const visible = Math.max(length - gap, MIN_VISIBLE);
    // Середина дольки: значок садится сюда, подпись — дальше по тому же лучу.
    const mid = turn(offset + length / 2, circumference);
    const start = turn(offset + gap / 2, circumference);
    const arc: ArcPlacement = {
      radius: R,
      mid,
      start,
      end: start + turn(visible, circumference),
      visible,
      gap,
      dash: `${visible} ${circumference - visible}`,
      // Начало сдвигаем на половину зазора — так дуга стоит по центру сектора,
      // и значок на его середине попадает ровно в её середину.
      dashOffset: -(offset + gap / 2),
    };
    offset += length;
    return { share, arc, icon: iconAt(mid, visible), label: labelAt(mid, share, width) };
  });
  dropCollidingLabels(slices);

  return {
    box: BOX, center: MID, radius: R, stroke: STROKE,
    ringInner: RING_INNER, ringOuter: RING_EDGE,
    circumference, slices,
  };
}

/** Снимает подписи, которые наезжают друг на друга.
 *
 *  Соседние дольки по 3% расходятся всего на 10.8°, это 17.5px хорды на радиусе
 *  подписи, а «3%» занимает 19.85px — они пересекаются по построению, и никакой
 *  порог `MIN_LABEL` этого не лечит: он про размер дольки, а сталкиваются
 *  подписи. Перебор по 205 тысячам распределений давал 1305 наложений, до 4.5px.
 *
 *  Жертву выбираем осознанно: идём от крупных долек к мелким, остаётся большая.
 *  Две слипшиеся подписи нечитаемы обе, а уцелевшая говорит правду; потерянная
 *  доля не исчезает из продукта — её сумма стоит в легенде под пирогом.
 */
function dropCollidingLabels(slices: SlicePlacement[]): void {
  const kept: Rect[] = [];
  for (const slice of [...slices].sort((a, b) => b.share - a.share)) {
    const label = slice.label;
    if (label === null) continue;
    if (kept.some((box) => overlap(box, label.box) > 0)) slice.label = null;
    else kept.push(label.box);
  }
}

function iconAt(mid: number, visible: number): IconPlacement | null {
  if (visible < MIN_ICON_ARC) return null;
  const x = MID + R * Math.sin(mid) - ICON_SHIFT;
  const y = MID - R * Math.cos(mid) - ICON_SHIFT;
  return {
    x, y, scale: ICON_SCALE, size: ICON_SIZE,
    cx: x + ICON_SIZE / 2, cy: y + ICON_SIZE / 2,
    box: { x0: x, y0: y, x1: x + ICON_SIZE, y1: y + ICON_SIZE },
  };
}

function labelAt(
  mid: number, share: number, width: (text: string) => number,
): LabelPlacement | null {
  if (share < MIN_LABEL) return null;
  const text = `${share}%`;
  const sin = Math.sin(mid);
  const down = -Math.cos(mid);          // проекция луча на ось Y экрана
  const x = MID + LABEL_R * sin;
  const y = MID + LABEL_R * down;
  // Куда подписи расти от точки привязки: наружу от кольца, а не во все
  // стороны. Порог 0.25 — чтобы у самой верхушки и низа подпись всё-таки
  // центрировалась, иначе она уезжает вбок от своей дольки.
  const anchor: TextAnchor = sin > 0.25 ? "start" : sin < -0.25 ? "end" : "middle";
  const baseline: TextBaseline =
    down > 0.25 ? "hanging" : down < -0.25 ? "alphabetic" : "central";
  const span = width(text);
  const height = LABEL_CAP;
  const x0 = anchor === "start" ? x : anchor === "end" ? x - span : x - span / 2;
  const y0 = baseline === "hanging" ? y
    : baseline === "alphabetic" ? y - height
    : y - height / 2;
  return {
    text, x, y, anchor, baseline, width: span, height,
    box: { x0, y0, x1: x0 + span, y1: y0 + height },
  };
}

/** Площадь пересечения габаритов; 0 — не касаются или касаются краем. */
export function overlap(a: Rect, b: Rect): number {
  const w = Math.min(a.x1, b.x1) - Math.max(a.x0, b.x0);
  const h = Math.min(a.y1, b.y1) - Math.max(a.y0, b.y0);
  return w > 0 && h > 0 ? w * h : 0;
}

/** Расстояние от центра пончика до ближней точки габарита. */
export function innerRadius(rect: Rect): number {
  const dx = Math.max(rect.x0 - MID, 0, MID - rect.x1);
  const dy = Math.max(rect.y0 - MID, 0, MID - rect.y1);
  return Math.hypot(dx, dy);
}

/** Четыре угла габарита — ими проверяется попадание значка в свою дугу. */
export function corners(rect: Rect): { x: number; y: number }[] {
  return [
    { x: rect.x0, y: rect.y0 }, { x: rect.x1, y: rect.y0 },
    { x: rect.x1, y: rect.y1 }, { x: rect.x0, y: rect.y1 },
  ];
}

/** Полярные координаты точки относительно центра: угол от 12 часов по часовой. */
export function polar(x: number, y: number): { angle: number; radius: number } {
  const dx = x - MID;
  const dy = y - MID;
  const angle = Math.atan2(dx, -dy);
  return { angle: angle < 0 ? angle + 2 * Math.PI : angle, radius: Math.hypot(dx, dy) };
}
